import React from 'react';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Contact() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <BrandLink />
          <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Retour à l'accueil
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-12"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center bg-emerald-100 p-4 rounded-2xl mb-4">
              <Send className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Nous contacter
            </h1>
            <p className="text-lg text-slate-600">
              Une question ? Besoin d'aide ? Écris-nous, on vous répond rapidement !
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Email Card */}
            <motion.a
              href="mailto:contact@tresopilot.fr"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 hover:shadow-lg hover:border-blue-300 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-xl group-hover:bg-blue-200 transition-colors">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">
                    Envoyer un email
                  </h3>
                  <p className="text-blue-800 font-semibold mb-2">
                    contact@tresopilot.fr
                  </p>
                  <p className="text-slate-600 text-sm">
                    Pour toutes vos questions, demandes de support ou suggestions.
                  </p>
                </div>
              </div>
            </motion.a>

            {/* WhatsApp Card */}
            <motion.a
              href="https://wa.me/33756901580"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 hover:shadow-lg hover:border-green-300 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="bg-green-100 p-3 rounded-xl group-hover:bg-green-200 transition-colors">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">
                    Contacter sur WhatsApp
                  </h3>
                  <p className="text-green-800 font-semibold mb-2">
                    +33 7 56 90 15 80
                  </p>
                  <p className="text-slate-600 text-sm">
                    Réponse rapide pour une assistance personnalisée.
                  </p>
                </div>
              </div>
            </motion.a>
          </div>

          {/* Support Hours */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-50 rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-start gap-4">
              <div className="bg-slate-200 p-3 rounded-xl">
                <Clock className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Horaires de support
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Notre équipe est disponible du <strong>lundi au vendredi</strong>, de <strong>9h à 18h</strong> (heure de Paris).
                </p>
                <p className="text-slate-600 text-sm mt-2">
                  Nous nous efforçons de répondre à tous les messages dans un délai de <strong>24 à 72 heures ouvrées</strong>.
                </p>
              </div>
            </div>
          </motion.div>

          {/* FAQ Suggestion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 bg-emerald-50 border-l-4 border-emerald-500 rounded-xl p-6"
          >
            <p className="text-emerald-900 font-semibold mb-2">
              💡 Avant de nous contacter
            </p>
            <p className="text-emerald-800 leading-relaxed">
              Consultez notre <strong>page Avertissement</strong> et nos <strong>CGU</strong> qui répondent 
              aux questions fréquentes sur le fonctionnement de TrésoPilot, les calculs d'estimations, 
              et vos responsabilités.
            </p>
            <div className="flex gap-3 mt-4">
              <a href="/disclaimer" className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm">
                → Avertissement
              </a>
              <a href="/cgu" className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm">
                → CGU
              </a>
            </div>
          </motion.div>

          <div className="border-t border-slate-200 pt-6 mt-8 text-center text-slate-500 text-sm space-y-2">
            <p><Tagline /></p>
            <p>© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}